import React, { useState } from "react";
import { toast } from "sonner";
import { Send, ExternalLink, Mail, Calendar, FileSpreadsheet, ShieldCheck, Building, Layers } from "lucide-react";
import { setDokumentStatus, setDatenraum, fetchMailtoDraft, markDatenraumEinladung } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const DOK_LIST = [
  { key: "teaser",    label: "Teaser",     icon: FileSpreadsheet, opts: ["nicht_versandt", "versandt", "geoeffnet"] },
  { key: "nda",       label: "NDA",        icon: ShieldCheck,     opts: ["nicht_versandt", "versandt", "unterzeichnet"] },
  { key: "expose",    label: "Exposé",     icon: Building,        opts: ["nicht_versandt", "versandt"] },
  { key: "im",        label: "IM",         icon: Layers,          opts: ["nicht_versandt", "versandt"] },
  { key: "rent_roll", label: "Rent Roll",  icon: Layers,          opts: ["nicht_versandt", "versandt"] },
];

const STATUS_STYLE = {
  nicht_versandt: { bg: "#F3F4F6", color: "#6B7280", border: "#D1D5DB", label: "nicht versandt" },
  versandt:       { bg: "#C4922A", color: "#FFFFFF", border: "#A6791F", label: "versandt" },
  geoeffnet:      { bg: "#022750", color: "#FFFFFF", border: "#022750", label: "geöffnet" },
  unterzeichnet:  { bg: "#0a8159", color: "#FFFFFF", border: "#0a8159", label: "unterzeichnet" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.nicht_versandt;
  return (
    <span
      className="inline-flex items-center font-sans text-[10px] uppercase tracking-widish px-2 py-0.5"
      style={{ backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {s.label}
    </span>
  );
};

const DocRow = ({ doc, data, onChange, onMailto }) => {
  const cur = data?.dokument_status?.[doc.key] || { status: "nicht_versandt" };
  const [status, setStatus] = useState(cur.status);
  const [datum, setDatum] = useState(cur.versandt_am || cur.unterzeichnet_am || new Date().toISOString().slice(0, 10));
  const [kommentar, setKommentar] = useState(cur.kommentar || "");
  const [saving, setSaving] = useState(false);
  const Icon = doc.icon;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { dokument: doc.key, status, kommentar };
      if (status !== "nicht_versandt") {
        if (doc.key === "nda" && status === "unterzeichnet") payload.unterzeichnet_am = datum;
        payload.versandt_am = datum;
      }
      await onChange(payload);
      toast.success(`${doc.label}: ${STATUS_STYLE[status].label}.`);
    } catch (e) { toast.error("Konnte Status nicht setzen"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-navy/15 p-3 space-y-3" data-testid={`docrow-${doc.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2">
          <Icon size={15} className="text-navy/70" strokeWidth={1.7}/>
          <div className="font-sans text-sm text-navy font-medium">{doc.label}</div>
        </div>
        <StatusBadge status={cur.status} />
      </div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4">
          <label className="pb-label">Status</label>
          <select data-testid={`docrow-${doc.key}-status`} className="pb-input--full" value={status} onChange={(e) => setStatus(e.target.value)}>
            {doc.opts.map((o) => <option key={o} value={o}>{STATUS_STYLE[o]?.label || o}</option>)}
          </select>
        </div>
        <div className="col-span-4">
          <label className="pb-label">Datum</label>
          <input type="date" data-testid={`docrow-${doc.key}-datum`} className="pb-input--full" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </div>
        <div className="col-span-4 flex gap-2 justify-end">
          <button data-testid={`docrow-${doc.key}-save`} onClick={handleSave} disabled={saving} className="pb-btn-primary text-xs px-3 py-1.5">
            {saving ? "…" : "Aktualisieren"}
          </button>
        </div>
        <div className="col-span-12">
          <input className="pb-input--full" placeholder="Kommentar (optional)" value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
        </div>
      </div>
      {(doc.key === "nda" || doc.key === "expose") && (
        <div className="pt-1">
          <button
            data-testid={`docrow-${doc.key}-mailto`}
            onClick={() => onMailto(doc.key === "expose" ? "expose" : "nda")}
            className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
          >
            <Mail size={12}/> Mail-Vorlage öffnen
          </button>
        </div>
      )}
    </div>
  );
};

export default function DocsTab({ data, onUpdate }) {
  const [mailtoPreview, setMailtoPreview] = useState(null);
  const dr = data.datenraum || { freigeschaltet: false, freigeschaltet_am: null, link: null, kommentar: null };
  const [drLink, setDrLink] = useState(dr.link || "");
  const [drKommentar, setDrKommentar] = useState(dr.kommentar || "");
  const [drSaving, setDrSaving] = useState(false);

  const handleDocChange = async (payload) => {
    const updated = await setDokumentStatus(data.id, payload);
    onUpdate(updated);
  };

  const handleMailto = async (typ) => {
    try {
      const draft = await fetchMailtoDraft(data.id, typ);
      setMailtoPreview({ ...draft, typ });
    } catch { toast.error("Mail-Vorlage konnte nicht geladen werden"); }
  };

  const handleDatenraumToggle = async (val) => {
    setDrSaving(true);
    try {
      const updated = await setDatenraum(data.id, {
        freigeschaltet: val,
        link: drLink || null,
        kommentar: drKommentar || null,
      });
      onUpdate(updated);
      toast.success(val ? "Datenraum freigeschaltet." : "Datenraum-Freigabe entfernt.");
    } catch { toast.error("Datenraum konnte nicht aktualisiert werden"); }
    finally { setDrSaving(false); }
  };

  const handleDatenraumSave = async () => {
    setDrSaving(true);
    try {
      const updated = await setDatenraum(data.id, {
        link: drLink || null,
        kommentar: drKommentar || null,
      });
      onUpdate(updated);
      toast.success("Datenraum gespeichert.");
    } catch { toast.error("Speichern fehlgeschlagen"); }
    finally { setDrSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="docs-tab">
      {/* Dokument-Versand-Matrix */}
      <section data-testid="docs-versand-matrix">
        <div className="pb-eyebrow mb-2">Versand-Matrix</div>
        <div className="grid grid-cols-1 gap-2">
          {DOK_LIST.map((d) => (
            <DocRow key={d.key} doc={d} data={data} onChange={handleDocChange} onMailto={handleMailto} />
          ))}
        </div>
      </section>

      {/* Datenraum */}
      <section data-testid="docs-datenraum">
        <div className="pb-eyebrow mb-2">Datenraum</div>
        <div className="border border-navy/15 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-sans text-sm text-navy font-medium">Zugang</div>
              {dr.freigeschaltet ? (
                <div className="text-navy/70 text-xs font-sans mt-0.5">
                  Freigeschaltet seit {fmtDate(dr.freigeschaltet_am)}
                </div>
              ) : (
                <div className="text-navy/55 text-xs font-sans mt-0.5">Nicht freigeschaltet</div>
              )}
            </div>
            <button
              data-testid="docs-datenraum-toggle"
              onClick={() => handleDatenraumToggle(!dr.freigeschaltet)}
              disabled={drSaving}
              className={dr.freigeschaltet ? "pb-btn-outline" : "pb-btn-primary"}
            >
              {dr.freigeschaltet ? "Zugang entziehen" : "Zugang freischalten"}
            </button>
          </div>
          <div>
            <label className="pb-label">Datenraum-Link</label>
            <input
              data-testid="docs-datenraum-link"
              className="pb-input--full"
              placeholder="https://drooms.com/..."
              value={drLink}
              onChange={(e) => setDrLink(e.target.value)}
            />
          </div>
          <div>
            <label className="pb-label">Kommentar</label>
            <textarea
              rows={2}
              className="pb-input--full"
              placeholder="Optional"
              value={drKommentar}
              onChange={(e) => setDrKommentar(e.target.value)}
            />
          </div>
          <div className="flex justify-between items-center">
            {dr.link ? (
              <a
                data-testid="docs-datenraum-open"
                href={dr.link} target="_blank" rel="noreferrer"
                className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
              >
                <ExternalLink size={12}/> Datenraum öffnen
              </a>
            ) : <span/>}
            <div className="flex gap-2">
              <button
                data-testid="docs-datenraum-einladung"
                onClick={() => handleMailto("datenraum_einladung")}
                disabled={!dr.link || !dr.freigeschaltet}
                title={!dr.link ? "Bitte erst Datenraum-Link eintragen" :
                       !dr.freigeschaltet ? "Bitte erst Zugang freischalten" :
                       "Einladungs-Mail vorbereiten"}
                className="pb-btn-accent text-xs gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Mail size={12}/> Einladung vorbereiten
              </button>
              <button data-testid="docs-datenraum-save" onClick={handleDatenraumSave} disabled={drSaving} className="pb-btn-primary text-xs px-3 py-1.5">
                Speichern
              </button>
            </div>
          </div>

          {/* Datenraum-Zugriffs-Log */}
          {(dr.zugriffs_log || []).length > 0 && (
            <details data-testid="docs-datenraum-log" className="border-t border-navy/10 pt-3">
              <summary className="text-xs uppercase tracking-widish text-navy/60 font-sans cursor-pointer hover:text-navy">
                Zugriffs-Log ({dr.zugriffs_log.length})
              </summary>
              <ul className="mt-2 space-y-2">
                {[...dr.zugriffs_log].reverse().map((z, i) => (
                  <li key={i} className="flex items-start justify-between text-xs font-sans border-l-2 border-gold/40 pl-2 py-1">
                    <div>
                      <div className="text-navy">
                        <strong>{({einladung_versandt: "Einladung versandt", link_geaendert: "Link geändert", freigeschaltet: "Freigeschaltet", gesperrt: "Gesperrt"})[z.aktion] || z.aktion}</strong>
                        {z.kommentar && <span className="text-navy/55"> · {z.kommentar}</span>}
                      </div>
                      <div className="text-navy/55 mt-0.5">
                        <span className="font-medium">{z.akteur}</span> · {(() => { try { return new Date(z.timestamp).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }); } catch { return z.timestamp; } })()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </section>

      {/* Mailto-Preview Modal */}
      {mailtoPreview && (
        <>
          <div onClick={() => setMailtoPreview(null)} className="fixed inset-0 bg-navy/40 z-[60]" />
          <div data-testid="mailto-preview" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,95vw)] max-h-[80vh] bg-white border border-navy/15 z-[70] flex flex-col">
            <div className="border-b border-navy/10 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="pb-eyebrow">Mail-Vorlage</div>
                <div className="font-serif text-navy text-xl mt-1">{mailtoPreview.preview.subject}</div>
              </div>
              <button onClick={() => setMailtoPreview(null)} className="text-navy/60 hover:text-navy">✕</button>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-3 text-sm font-sans">
              <div>
                <div className="pb-label">An</div>
                <div className="text-navy" data-testid="mailto-to">{mailtoPreview.preview.to || "(noch keine E-Mail erfasst)"}</div>
              </div>
              <div>
                <div className="pb-label">Betreff</div>
                <div className="text-navy">{mailtoPreview.preview.subject}</div>
              </div>
              <div>
                <div className="pb-label">Text</div>
                <pre className="text-navy whitespace-pre-wrap font-sans bg-ledger p-3 border border-navy/10 text-[13px] leading-relaxed">
{mailtoPreview.preview.body}
                </pre>
              </div>
            </div>
            <div className="border-t border-navy/10 px-5 py-3 flex justify-between items-center">
              <span className="text-xs text-navy/55 font-sans">Öffnet im Standard-Mail-Client</span>
              <div className="flex gap-2">
                <button onClick={() => setMailtoPreview(null)} className="pb-btn-outline">Schließen</button>
                <a
                  data-testid="mailto-open-client"
                  href={mailtoPreview.mailto_url}
                  onClick={async () => {
                    // Bei Datenraum-Einladung: Backend-Marker + zugriffs_log-Eintrag setzen
                    if (mailtoPreview.typ === "datenraum_einladung") {
                      try {
                        const updated = await markDatenraumEinladung(data.id, drKommentar || null);
                        onUpdate(updated);
                        toast.success("Einladung vorbereitet — Zugriffs-Log aktualisiert");
                      } catch { toast.error("Einladungs-Marker konnte nicht gesetzt werden"); }
                    }
                    setTimeout(() => setMailtoPreview(null), 200);
                  }}
                  className="pb-btn-primary gap-2"
                >
                  <Send size={14}/> Mail-Client öffnen
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
